// src/pages/SuperAdminHomePage.tsx

import CancelIcon from "@mui/icons-material/Cancel";
import CategoryIcon from "@mui/icons-material/Category";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PaidIcon from "@mui/icons-material/Paid";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { apiRequest } from "../utils/apiClient";

const KALAMATA_GREEN = "#0B3D2E";
const LIGHT_BG = "#F8F9FA";

const formatCurrency = (value: number) =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const StatCard = ({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}) => (
  <Card
    sx={{
      borderRadius: 3,
      boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
      height: "100%",
    }}
  >
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ color: color, mt: 1 }}
          >
            {value}
          </Typography>
        </Box>
        {icon}
      </Box>
    </CardContent>
  </Card>
);

const SuperAdminHomePage = () => {
  const [stats, setStats] = useState<any>(null);

  const getStats = async () => {
    try {
      const response = await apiRequest("/api/stats/inventory", "POST");
      if (response.status !== 200) {
        throw new Error(response.message);
      }
      setStats(response.data);
    } catch (error) {
      console.error("Error getting stats:", error);
    }
  };

  useEffect(() => {
    getStats();
  }, []);

  const totalCost = Number(stats?.totalCost || 0);
  const totalMsrp = Number(stats?.totalMsrp || 0);
  const totalProfit = totalMsrp - totalCost;

  return (
    <Box sx={{ p: 4, backgroundColor: LIGHT_BG, height: "82vh" }}>
      {/* Header */}
      <Typography variant="h4" fontWeight="bold" mb={1} color="#8f8e8eff">
        Inventory Management Dashboard
      </Typography>
      <Divider sx={{ mb: 4}}/>
      {/* KPI Section */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Total Inventory Cost"
            value={`$ ${formatCurrency(totalCost)}`}
            icon={<PaidIcon sx={{ fontSize: 40, color: KALAMATA_GREEN }} />}
            color={KALAMATA_GREEN}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Total MSRP Value"
            value={`$ ${formatCurrency(totalMsrp)}`}
            icon={<TrendingUpIcon sx={{ fontSize: 40, color: "#2E7D32" }} />}
            color="#2E7D32"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Gross Profit Potential"
            value={`$ ${formatCurrency(totalProfit)}`}
            icon={<Inventory2Icon sx={{ fontSize: 40, color: "#1565C0" }} />}
            color="#1565C0"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Total Products"
            value={stats?.totalProducts || 0}
            icon={<CategoryIcon sx={{ fontSize: 40, color: "#6A1B9A" }} />}
            color="#6A1B9A"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Low Stock Items"
            value={stats?.lowStock || 0}
            icon={<WarningAmberIcon sx={{ fontSize: 40, color: "#ED6C02" }} />}
            color="#ED6C02"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Out of Stock"
            value={stats?.outOfStock || 0}
            icon={<CancelIcon sx={{ fontSize: 40, color: "#D32F2F" }} />}
            color="#D32F2F"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <StatCard
            title="Categories"
            value={stats?.totalCategories || 7}
            icon={<CategoryIcon sx={{ fontSize: 40, color: "#424242" }} />}
            color="#424242"
          />
        </Grid>
      </Grid>

      {/* Category Performance Section */}
      {/* <Box mt={6}>
        <Typography variant="h5" fontWeight="bold" mb={3}>
          Category Performance
        </Typography>

        <Card
          sx={{ borderRadius: 3, boxShadow: "0 6px 18px rgba(0,0,0,0.08)" }}
        >
          <CardContent>
            {(stats?.categories || []).map((cat: any, index: number) => {
              const percentage =
                cat.totalMsrp > 0
                  ? Math.round((cat.totalCost / cat.totalMsrp) * 100)
                  : 0;

              return (
                <Box key={index} mb={3}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography fontWeight="bold">{cat.name}</Typography>

                    <Chip
                      label={`$ ${formatCurrency(cat.totalMsrp)}`}
                      sx={{
                        backgroundColor: KALAMATA_GREEN,
                        color: "white",
                      }}
                    />
                  </Box>

                  <Box mt={1}>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 8,
                        borderRadius: 5,
                        backgroundColor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: KALAMATA_GREEN,
                        },
                      }}
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      Cost: $ {formatCurrency(cat.totalCost)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Stock: {cat.totalStock}
                    </Typography>
                  </Box>

                  {index !== stats.categories.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Box> */}
    </Box>
  );
};

export default SuperAdminHomePage;
